import React from 'react';
import Moment from 'moment';
import PropTypes from 'prop-types';
import Avatar from '@mui/material/Avatar';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Pagination from '@mui/material/Pagination';
import ListSubheader from '@mui/material/ListSubheader';
import AddIcon from '@mui/icons-material/Add';
import {
  useSelector,
  useDispatch,
} from 'react-redux';

import {
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ContentCopy,
  Share,
} from '@mui/icons-material';
import AddEditModal from './components/AddEditModal';
import CardLoading from './components/CardLoading';
import { getHorsePublicShares } from '../../../../../redux/horsePublicShare/actions';

const ShareLinksCardComponent = ({ id }) => {
  const dispatch = useDispatch();

  // Redux state
  const isFetchingHorsePublicShares = useSelector((state) => state.horsePublicShare.isFetchingHorsePublicShares);
  const horsePublicShares = useSelector((state) => state.horsePublicShare.horsePublicShares);
  const selectedBarn = useSelector((state) => state.barn.selectedBarn);

  // State
  const [openAddEditModal, setOpenAddEditModal] = React.useState(false);
  const [selectedHorsePublicShare, setSelectedHorsePublicShare] = React.useState(null);
  const [pageSize] = React.useState(10);
  const [page, setPage] = React.useState(1);
  const [sortModel] = React.useState([
    {
      field: 'startDate',
      sort: 'desc',
    },
  ]);
  const [openCopied, setOpenCopied] = React.useState(false)

  // const roles = UserService.getUserRolesForTenant(
  //   user,
  //   selectedBarn.tenant,
  // );
  // // eslint-disable-next-line no-unused-vars
  // const isAdmin = isUserAdmin(roles) || isUserOwner(roles);

  React.useEffect(() => {
    dispatch(getHorsePublicShares(id, (page - 1), pageSize, sortModel.map((s) => `&sort=${s.field},${s.sort}`)));
  }, [id, page]);

  const handleEditClick = (horsePublicShare) => {
    setSelectedHorsePublicShare(horsePublicShare);
    setOpenAddEditModal(true);
  };

  const handleAddClick = () => {
    setSelectedHorsePublicShare(null);
    setOpenAddEditModal(true);
  };

  const handleCloseAddEditModal = () => {
    setSelectedHorsePublicShare(null)
    setOpenAddEditModal(false);
  };

  return (
    <>
      {isFetchingHorsePublicShares
        ? <CardLoading />
        : (
          <Card>
            <CardHeader
              avatar={<Avatar><Share /></Avatar>}
              action={(
                <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleAddClick}>
                  New Share Link
                </Button>
              )}
              title="Horse Profile Sharing Links"
              subheader="Used to share information about this horse with outside individuals for sales, discharges, etc."
              subheaderTypographyProps={{ sx: { textWrap: 'auto' } }}
            />
            <CardContent>
              <List
                aria-label="horse current location"
                subheader={(
                  <ListSubheader component="div" id="nested-list-subheader-upcoming">
                    Current Sharing Links
                  </ListSubheader>
                )}
              >
                {
                  horsePublicShares.content.length > 0
                    ? horsePublicShares.content.map(((horsePublicShare) => (
                      <ListItem
                        key={horsePublicShare.id}
                        secondaryAction={(
                          <Tooltip title="Copy Share Link">
                            <IconButton edge="end" aria-label="edit" onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpenCopied(true); navigator.clipboard.writeText(`https://${selectedBarn.tenant}.questri.app/public/horses/${id}/shared/${horsePublicShare.id}`); }}>
                              <ContentCopy />
                            </IconButton>
                          </Tooltip>
                        )}
                        disablePadding
                        onClick={() => { handleEditClick(horsePublicShare); }}
                        divider
                      >
                        <ListItemButton>
                          <ListItemIcon><Share /></ListItemIcon>
                          <ListItemText
                            primary={`${horsePublicShare.shareNote || 'Horse Profile Sharing Link'}`}
                            secondary={(
                              <React.Fragment>
                                <Typography
                                  sx={{ display: 'block' }}
                                  component="span"
                                  variant="body2"
                                >
                                  <a target="blank" href={`https://${selectedBarn.tenant}.questri.app/public/horses/${id}/shared/${horsePublicShare.id}`}>{`https://${selectedBarn.tenant}.questri.app/public/horses/${id}/shared/${horsePublicShare.id}`}</a>
                                </Typography>
                                <Typography>
                                  {`Link active ${Moment(horsePublicShare.startDate).format('ll')} ${horsePublicShare.endDate
                                    ? (`through ${Moment(horsePublicShare.endDate).format('ll')}`)
                                    : 'through no end date specified'}`}
                                </Typography>
                              </React.Fragment>
                            )}
                            secondaryTypographyProps={{ sx: { textWrap: 'auto' } }}
                            primaryTypographyProps={{
                              variant: 'h5',
                              maxWidth: '50%',
                              noWrap: true,
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    )))
                    : (
                      <ListItemButton disabled><ListItemText secondary="No sharing links for this horse" /></ListItemButton>
                    )
                }
              </List>

              <Pagination count={horsePublicShares.totalPages} variant="outlined" page={page} onChange={(event, value) => { setPage(value); }} disabled={isFetchingHorsePublicShares} />
            </CardContent>
            <Snackbar
              open={openCopied}
              autoHideDuration={3000}
              onClose={() => setOpenCopied(false)}
              message="Sharing Link Copied!"
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
            />
          </Card>
        )}
      <AddEditModal
        open={openAddEditModal}
        horsePublicShare={selectedHorsePublicShare}
        horseId={id}
        onClose={handleCloseAddEditModal}
      />
    </>
  );
};

ShareLinksCardComponent.propTypes = { id: PropTypes.string.isRequired };
ShareLinksCardComponent.defaultProps = {};

export default ShareLinksCardComponent;
